/* eslint-disable no-console */
import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  TableContainer,
} from "@mui/material";

function groupMatchesBySection(matches = []) {
  const grouped = {};
  matches.forEach((m) => {
    const sec = m.section || "NoSection";
    if (!grouped[sec]) grouped[sec] = [];
    grouped[sec].push(m);
  });
  return grouped;
}

function generateScoringGrid(maxPoints) {
  const rows = Math.ceil(maxPoints / 11);
  const content = [];
  for (let r = 0; r < rows; r += 1) {
    const rowCells = [];
    for (let c = 0; c < 11; c += 1) {
      const pointVal = r * 11 + (c + 1);
      rowCells.push(
        <TableCell
          key={c}
          align="center"
          sx={{
            width: "14px",
            height: "14px",
            border: "1px solid #ddd",
            fontSize: "14px",
            padding: 0,
          }}
        >
          {pointVal <= maxPoints ? pointVal : ""}
        </TableCell>
      );
    }
    content.push(<TableRow key={r}>{rowCells}</TableRow>);
  }
  return content;
}


function getDisplayName(originalName, fillInData) {
  if (fillInData && fillInData.subId && fillInData.fillInName) {
    return (
      <>
        {fillInData.fillInName}{" "}
        <span style={{ fontStyle: "italic", fontSize: "12px" }}>
          ({originalName})
        </span>
      </>
    );
  }
  return originalName;
}

function getTeamDisplayName(team, fillIns, teamsMapping) {
  if (!team || !team.name) return "Bye";
  let members = [];
  if (team.members) {
    members = Object.values(team.members);
  } else if (teamsMapping && teamsMapping[team.id] && teamsMapping[team.id].members) {
    members = Object.values(teamsMapping[team.id].members);
  }
  const fillInTexts = [];
  members.forEach((member) => {
    if (fillIns && fillIns[member.id]) {
      const fillInData = fillIns[member.id];
      fillInTexts.push(
        `${fillInData.fillInName} fill-in for ${member.lastName}`
      );
    }
  });
  return (
    <>
      {team.name}
      {fillInTexts.length > 0 && (
        <Typography variant="caption" display="block">
          {fillInTexts.join(", ")}
        </Typography>
      )}
    </>
  );
}

function renderHeaderAndInfo({
  date,
  name,
  participantType,
  week,
  scoringSystem,
  pointsPerGame,
  gamesPerSet,
  tieBreakerRule,
  matchFormat,
  servesPerRally,
  section,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "1mm",
        padding: "2mm",
        border: "1px solid #ddd",
        backgroundColor: "#f9f9f9",
      }}
    >
      <Box sx={{ width: "25%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            {date}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            {name} {participantType}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Week {week}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Section {section}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: "40%", textAlign: "center" }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: "bold" }}>
          Ballarat Squash and Racquetball Association
        </Typography>
        <Typography sx={{ fontSize: "10px" }}>
          www.ballaratsquashandracquetball.com
        </Typography>
      </Box>

      <Box sx={{ width: "25%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Scoring:
          </Typography>
          <Typography sx={{ fontSize: "12px", textAlign: "right" }}>
            {scoringSystem} to {pointsPerGame}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Tiebreak:
          </Typography>
          <Typography sx={{ fontSize: "12px", textAlign: "right" }}>
            {tieBreakerRule || "N/A"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Games:
          </Typography>
          <Typography sx={{ fontSize: "12px", textAlign: "right" }}>
            {matchFormat || "Play all"} {gamesPerSet}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "3px" }}>
          <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
            Serves:
          </Typography>
          <Typography sx={{ fontSize: "12px", textAlign: "right" }}>
            {servesPerRally}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function PrintableSinglesScoreSheet({
  pennantData,
  weekData,
  fillIns,
  playType,
  teamsMapping,
}) {
  const { name = "", participantType = "", scoringType = {} } = pennantData || {};
  const {
    scoringSystem,
    pointsPerGame,
    gamesPerSet,
    tieBreakerRule,
    matchFormat,
    servesPerRally,
  } = scoringType;
  const { date, week, matches = [], section } = weekData || {};

  const sectionsMap = groupMatchesBySection(matches);
  const handlePrint = () => window.print();

  return (
    <Box sx={{ padding: 2 }}>
      <Box className="no-print" sx={{ textAlign: "center", marginBottom: 2 }}>
        <Button variant="contained" onClick={handlePrint}>
          Print Scoresheet
        </Button>
      </Box>

      {Object.entries(sectionsMap).map(([secKey, sectionMatches]) => (
        <Box key={secKey} sx={{ pageBreakAfter: "always", marginBottom: 4 }}>
          {renderHeaderAndInfo({
            date,
            name,
            participantType,
            week,
            scoringSystem,
            pointsPerGame,
            gamesPerSet,
            tieBreakerRule,
            matchFormat,
            servesPerRally,
            section,
          })}
          {sectionMatches.map((match, mIndex) => {
            const team1 = match.team1 || {};
            const team2 = match.team2 || {};

            let p1Name;
            let p2Name;
            if (playType === "Doubles") {
              p1Name = team1.id
                ? getTeamDisplayName(team1, fillIns, teamsMapping)
                : team1.name || "Bye";
              p2Name = team2.id
                ? getTeamDisplayName(team2, fillIns, teamsMapping)
                : team2.name || "Bye";
            } else {
              p1Name = team1.id
                ? getDisplayName(team1.name || "Bye", fillIns[team1.id])
                : team1.name || "Bye";
              p2Name = team2.id
                ? getDisplayName(team2.name || "Bye", fillIns[team2.id])
                : team2.name || "Bye";
            }

            return (
              <Box
                key={mIndex}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  border: "1px solid #000",
                  borderBottom: "3px solid #000",
                  pageBreakInside: "avoid",
                  marginBottom: "10px",
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">
                  Court: {match.courtNumber ?? '....'}
                </Typography>
              </Box>

                <Box sx={{ width: "48%", display: "flex", flexDirection: "column" }}>
                  <TableContainer component={Paper} elevation={0} sx={{ boxShadow: "none", borderRadius: 0 }}>
                    <Table size="small">
                      <TableBody>
                        {Array.from({ length: Number(gamesPerSet) }).map((_, rowIdx) => (
                          <TableRow key={`leftRow-${rowIdx}`} sx={{ borderBottom: "3px solid #ddd" }}>
                            <Table size="small" sx={{ width: "100%" }}>
                              <TableBody>{generateScoringGrid(Number(pointsPerGame))}</TableBody>
                            </Table>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                width: "10%",
                                height: "18px",
                                textAlign: "center",
                              }}
                            />
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ width: "100%", textAlign: "center" }}>
                    <Table size="small" sx={{ width: "100%" }}>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: "14px",
                              border: "1px solid #ddd",
                              padding: "3px",
                              width: "60%",
                            }}
                          >
                            {p1Name}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "3px",
                              border: "1px solid #ddd",
                              width: "20%",
                              height: "20px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                              Games:
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "3px",
                              border: "1px solid #ddd",
                              width: "20%",
                              height: "20px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                              Points:
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Box>

                <Box sx={{ width: "48%", display: "flex", flexDirection: "column" }}>
                  <TableContainer component={Paper} elevation={0} sx={{ boxShadow: "none", borderRadius: 0 }}>
                    <Table size="small">
                      <TableBody>
                        {Array.from({ length: Number(gamesPerSet) }).map((_, rowIdx) => (
                          <TableRow key={`rightRow-${rowIdx}`} sx={{ borderBottom: "3px solid #ddd" }}>
                            <Table size="small" sx={{ width: "100%" }}>
                              <TableBody>{generateScoringGrid(Number(pointsPerGame))}</TableBody>
                            </Table>
                            <TableCell
                              sx={{
                                border: "1px solid #ddd",
                                width: "10%",
                                height: "18px",
                                textAlign: "center",
                              }}
                            />
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ width: "100%", textAlign: "center" }}>
                    <Table size="small" sx={{ width: "100%" }}>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontWeight: "bold",
                              fontSize: "14px",
                              border: "1px solid #ddd",
                              padding: "3px",
                              width: "60%",
                            }}
                          >
                            {p2Name}
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "3px",
                              border: "1px solid #ddd",
                              width: "20%",
                              height: "20px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                              Games:
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: "3px",
                              border: "1px solid #ddd",
                              width: "20%",
                              height: "20px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px", fontWeight: "bold" }}>
                              Points:
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}

      <style>
        {`
          @media print {
            @page {
              margin: 8mm;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
}

PrintableSinglesScoreSheet.propTypes = {
  pennantData: PropTypes.shape({
    name: PropTypes.string,
    participantType: PropTypes.string,
    scoringType: PropTypes.shape({
      scoringSystem: PropTypes.string,
      pointsPerGame: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      gamesPerSet: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tieBreakerRule: PropTypes.string,
      matchFormat: PropTypes.string,
      servesPerRally: PropTypes.string,
    }),
  }).isRequired,
  weekData: PropTypes.shape({
    date: PropTypes.string,
    week: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    matches: PropTypes.arrayOf(PropTypes.object),
    section: PropTypes.string,
  }).isRequired,
  fillIns: PropTypes.object,
  playType: PropTypes.string,
  teamsMapping: PropTypes.object,
};
